import { isEqual } from 'lodash';
import { ProductUpdateRequestDto, i18nResponseDto } from '@tiendanube/common';
import { ProductUpdateRequestApiV2Dto } from '@tiendanube/common/src/domains/catalog/interfaces';
import { LanguagesType } from 'App/i18n';
import { ProductFormState } from 'domains/Catalog/Products/pages/hooks/useProductForm';
import { generateCategoriesPayload } from './generateCategoriesPayload';
import { generateImagesPayload } from './generateImagesPayload';
import { generateRelatedProductsPayload } from './generateRelatedProductsPayload';
import { generateTagsPayload } from './generateTagsPayload';
import { getUpdateVariantsPayload } from './getUpdateVariantsPayload';
import { areEqualAttributes, getTags } from './helpers';
import { getAttributes } from '../shared/helpers';

// eslint-disable-next-line max-statements
export const getUpdateProductPayload = (
  originalProduct: ProductFormState,
  newProduct: ProductUpdateRequestDto,
  language: LanguagesType,
): Partial<ProductUpdateRequestApiV2Dto> => {
  const changes: Partial<ProductUpdateRequestApiV2Dto> = {};

  if (
    newProduct.name &&
    !isEqual(originalProduct.name[language], newProduct.name[language])
  ) {
    const name: i18nResponseDto = {
      ...newProduct.name,
      default: newProduct.name?.[language],
    };
    Object.assign(changes, { name, handle: name });
  }

  if (
    newProduct.description &&
    !isEqual(originalProduct.description, newProduct.description)
  ) {
    Object.assign(changes, {
      description: {
        ...newProduct.description,
        default: newProduct.description[language],
      },
    });
  }

  if (
    newProduct.attributes &&
    !areEqualAttributes(
      originalProduct.attributes,
      newProduct.attributes,
      language,
    )
  ) {
    Object.assign(changes, {
      attributes: getAttributes(language, newProduct.attributes),
    });
  }

  if (
    newProduct.categories &&
    !isEqual(originalProduct.categories, newProduct.categories)
  ) {
    const categoriesPayload = generateCategoriesPayload(
      originalProduct.categories,
      newProduct.categories,
    );
    Object.assign(changes, {
      categories: [...categoriesPayload],
    });
  }

  const variantsDifferences = getUpdateVariantsPayload(
    language,
    originalProduct.variants,
    newProduct.variants,
  );

  if (variantsDifferences.length > 0) {
    Object.assign(changes, {
      variants: [...variantsDifferences],
    });
  }

  if (
    newProduct.tags &&
    !isEqual(originalProduct.tags, getTags(newProduct.tags))
  ) {
    Object.assign(changes, {
      tags: generateTagsPayload(originalProduct.tags, getTags(newProduct.tags)),
    });
  }

  if (
    newProduct.seo_title &&
    !isEqual(originalProduct.seoTitle, newProduct.seo_title)
  ) {
    Object.assign(changes, {
      seo_title: {
        ...newProduct.seo_title,
        default: newProduct.seo_title[language],
      },
    });
  }

  if (
    newProduct.seo_description &&
    !isEqual(originalProduct.seoDescription, newProduct.seo_description)
  ) {
    Object.assign(changes, {
      seo_description: {
        ...newProduct.seo_description,
        default: newProduct.seo_description[language],
      },
    });
  }

  if (
    newProduct.video_url &&
    !isEqual(originalProduct.videoUrl, newProduct.video_url)
  ) {
    Object.assign(changes, { video_url: newProduct.video_url });
  }

  if (!isEqual(originalProduct.freeShipping, newProduct.free_shipping)) {
    Object.assign(changes, { free_shipping: newProduct.free_shipping });
  }

  const originalRequiresShipping = !originalProduct.isDigital;
  if (originalRequiresShipping !== newProduct.requires_shipping) {
    Object.assign(changes, { requires_shipping: newProduct.requires_shipping });
  }

  if (newProduct.brand && !isEqual(originalProduct.brand, newProduct.brand)) {
    Object.assign(changes, { brand: newProduct.brand });
  }

  if (!isEqual(originalProduct.published, newProduct.published)) {
    Object.assign(changes, { publish: newProduct.published });
  }

  if (newProduct.images) {
    Object.assign(changes, {
      images: generateImagesPayload(originalProduct.images, newProduct.images),
    });
  }

  if (newProduct.relatedProducts) {
    newProduct.relatedProducts.forEach((relatedProduct) => {
      if (relatedProduct.type === 'ALTERNATIVE') {
        const alternativeProductsIds =
          originalProduct.relatedProducts?.alternative?.map((product) =>
            Number(product.id),
          ) || [];

        Object.assign(changes, {
          alternative_products: generateRelatedProductsPayload(
            alternativeProductsIds,
            relatedProduct.related_product_ids,
          ),
        });
      }
      if (relatedProduct.type === 'COMPLEMENTARY') {
        const complementaryProductsIds =
          originalProduct.relatedProducts?.complementary?.map((product) =>
            Number(product.id),
          ) || [];

        Object.assign(changes, {
          complementary_products: generateRelatedProductsPayload(
            complementaryProductsIds,
            relatedProduct.related_product_ids,
          ),
        });
      }
    });
  }

  return { ...changes };
};
